import React, { useState, useEffect } from 'react';
import { X, Users, CheckCircle, Clock, FileText, Eye, Edit2, Calendar } from 'lucide-react';
import { questionnaireService } from '../services/questionnaireService';
import QuestionnaireResponseView from './QuestionnaireResponseView';
import type { Questionnaire, QuestionnaireAssignment, QuestionnaireResponse } from '../types/questionnaires';

interface QuestionnaireOverviewProps {
  questionnaire: Questionnaire;
  onClose: () => void;
}

const QuestionnaireOverview: React.FC<QuestionnaireOverviewProps> = ({ 
  questionnaire, 
  onClose 
}) => {
  const [assignments, setAssignments] = useState<QuestionnaireAssignment[]>([]);
  const [responses, setResponses] = useState<QuestionnaireResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState<QuestionnaireAssignment | null>(null);
  const [editingDeadlineId, setEditingDeadlineId] = useState<string | null>(null);
  const [newDeadline, setNewDeadline] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [questionnaire.id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Load assignments
      const assignmentData = await questionnaireService.getAssignmentsByQuestionnaire(questionnaire.id);
      setAssignments(assignmentData);
      
      // Load all responses
      const responseData = await questionnaireService.getResponsesByQuestionnaire(questionnaire.id);
      setResponses(responseData);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStartEditDeadline = (assignment: QuestionnaireAssignment) => {
    setEditingDeadlineId(assignment.id);
    const deadline = new Date(assignment.deadline);
    const formattedDate = deadline.toISOString().split('T')[0];
    setNewDeadline(formattedDate);
  };

  const handleCancelEditDeadline = () => {
    setEditingDeadlineId(null);
    setNewDeadline('');
  };

  const handleSaveDeadline = async (assignmentId: string) => {
    if (!newDeadline) return;

    try {
      const newDeadlineDate = new Date(newDeadline);
      await questionnaireService.updateAssignment(assignmentId, {
        deadline: newDeadlineDate
      });
      alert('✅ Deadline erfolgreich aktualisiert!');
      setEditingDeadlineId(null);
      setNewDeadline('');
      loadData();
    } catch (error) {
      console.error('Error updating deadline:', error);
      alert('Fehler beim Aktualisieren der Deadline');
    }
  };

  const getAssignmentResponse = (assignmentId: string) => {
    return responses.find(r => r.assignmentId === assignmentId);
  };

  const stats = {
    total: assignments.length,
    submitted: assignments.filter(a => a.status === 'submitted').length,
    pending: assignments.filter(a => a.status !== 'submitted').length,
    totalProjects: responses.reduce((sum, r) => sum + (r.projects?.length || 0), 0)
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">{questionnaire.title}</h2>
              <p className="text-sm text-gray-600 mb-3">
                {questionnaire.mode === 'quick' ? 'Schnellerfassung' : 'Vollständiger Fragebogen'}
              </p>
              
              {/* Stats */}
              <div className="flex gap-6 text-sm">
                <div className="flex items-center text-gray-700">
                  <Users className="w-4 h-4 mr-2" />
                  <span className="font-semibold mr-1">{stats.total}</span> Zuweisungen
                </div>
                <div className="flex items-center text-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span className="font-semibold mr-1">{stats.submitted}</span> Abgeschickt
                </div>
                <div className="flex items-center text-blue-700">
                  <Clock className="w-4 h-4 mr-2" />
                  <span className="font-semibold mr-1">{stats.pending}</span> Ausstehend
                </div>
                <div className="flex items-center text-purple-700">
                  <FileText className="w-4 h-4 mr-2" />
                  <span className="font-semibold mr-1">{stats.totalProjects}</span> Projekte gesamt
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Submitted Responses */}
              {assignments.filter(a => a.status === 'submitted').length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                    Abgeschickte Antworten ({stats.submitted})
                  </h3>
                  <div className="space-y-3">
                    {assignments
                      .filter(a => a.status === 'submitted')
                      .map(assignment => {
                        const response = getAssignmentResponse(assignment.id);
                        const projectCount = response?.projects?.length || 0;
                        
                        return (
                          <div
                            key={assignment.id}
                            className="bg-green-50 border-2 border-green-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 text-lg mb-2">
                                  {assignment.assignedTo}
                                </h4>
                                
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-600">Abgeschickt:</span>
                                    <span className="ml-2 font-medium text-green-700">
                                      {assignment.submittedAt && formatDate(assignment.submittedAt)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Projekte:</span>
                                    <span className="ml-2 font-medium text-purple-700">
                                      {projectCount} Projekt{projectCount !== 1 ? 'e' : ''}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Project titles preview */}
                                {response && response.projects && response.projects.length > 0 && (
                                  <div className="mt-3 text-sm text-gray-700">
                                    <span className="font-medium">Projekte:</span>
                                    <ul className="ml-4 mt-1 space-y-1">
                                      {response.projects.slice(0, 3).map((proj, idx) => (
                                        <li key={idx} className="text-gray-600">
                                          • {proj.projektTitel || `Projekt ${idx + 1}`}
                                        </li>
                                      ))}
                                      {response.projects.length > 3 && (
                                        <li className="text-gray-500 italic">
                                          ... und {response.projects.length - 3} weitere
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                              
                              <button
                                onClick={() => setSelectedAssignment(assignment)}
                                className="ml-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 font-medium"
                              >
                                <Eye className="w-4 h-4" />
                                Alle Antworten ansehen
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Pending Assignments */}
              {stats.pending > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-blue-600" />
                    Ausstehende Zuweisungen ({stats.pending})
                  </h3>
                  <div className="space-y-3">
                    {assignments
                      .filter(a => a.status !== 'submitted')
                      .map(assignment => {
                        const now = new Date();
                        const isPastDeadline = new Date(assignment.deadline) < now;
                        const response = getAssignmentResponse(assignment.id);
                        const hasStarted = response && response.projects && response.projects.length > 0;
                        
                        return (
                          <div
                            key={assignment.id}
                            className={`border-2 rounded-lg p-5 ${
                              isPastDeadline 
                                ? 'bg-red-50 border-red-200' 
                                : hasStarted
                                  ? 'bg-blue-50 border-blue-200'
                                  : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 mb-2">
                                  {assignment.assignedTo}
                                </h4>
                                
                                <div className="text-sm space-y-1">
                                  {/* Deadline - Editable */}
                                  {editingDeadlineId === assignment.id ? (
                                    <div className="flex items-center gap-2">
                                      <Calendar className="w-4 h-4" />
                                      <input
                                        type="date"
                                        value={newDeadline}
                                        onChange={(e) => setNewDeadline(e.target.value)}
                                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                      />
                                      <button
                                        onClick={() => handleSaveDeadline(assignment.id)}
                                        className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 transition-colors"
                                      >
                                        Speichern
                                      </button>
                                      <button
                                        onClick={handleCancelEditDeadline}
                                        className="bg-gray-300 text-gray-700 px-3 py-1 rounded text-xs hover:bg-gray-400 transition-colors"
                                      >
                                        Abbrechen
                                      </button>
                                    </div>
                                  ) : (
                                    <div className={`flex items-center gap-2 ${isPastDeadline ? 'text-red-700 font-medium' : 'text-gray-600'}`}>
                                      <Clock className="w-4 h-4" />
                                      <span>
                                        Deadline: {formatDate(assignment.deadline)}
                                        {isPastDeadline && ' (überschritten)'}
                                      </span>
                                      {assignment.status !== 'cancelled' && assignment.status !== 'submitted' && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleStartEditDeadline(assignment);
                                          }}
                                          className="text-purple-600 hover:text-purple-700 transition-colors"
                                          title="Deadline bearbeiten"
                                        >
                                          <Edit2 className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                  
                                  <div className="text-gray-600">
                                    Status: {hasStarted ? '🔵 In Bearbeitung' : '⚪ Noch nicht begonnen'}
                                  </div>
                                  
                                  {response && response.projects && response.projects.length > 0 && (
                                    <div className="text-purple-700 font-medium mt-2">
                                      📝 {response.projects.length} Projekt{response.projects.length !== 1 ? 'e' : ''} begonnen
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {hasStarted && (
                                <button
                                  onClick={() => setSelectedAssignment(assignment)}
                                  className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm font-medium"
                                >
                                  <Eye className="w-4 h-4" />
                                  Entwurf ansehen
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {stats.total === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p>Noch keine Zuweisungen für diesen Fragebogen</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Response Viewer Modal */}
      {selectedAssignment && (
        <QuestionnaireResponseView
          assignment={selectedAssignment}
          onClose={() => setSelectedAssignment(null)}
        />
      )}
    </div>
  );
};

export default QuestionnaireOverview;

